/**
 * /operations/eos/clearance-center — Session 3B PR 8.
 *
 * Owning agent: Tashghil. Cross-employee clearance dashboard.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BellRing, CheckCircle2, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { ClearanceCenterItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StatusFilter = "all" | "in_progress" | "stuck" | "complete";

function _statusToneClass(s: ClearanceCenterItem["status"]): string {
  if (s === "stuck") return "bg-rose-100 text-rose-800";
  if (s === "complete") return "bg-emerald-100 text-emerald-800";
  return "bg-amber-100 text-amber-800";
}

export function ExitClearanceCenterPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const list = useQuery({
    queryKey: ["session3b-clearance-center", legalEntityId],
    queryFn: () => session3bApi.listClearanceCenter(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: ClearanceCenterItem[] = list.data?.items ?? [];
  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((r) => r.status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(() => {
    const stuck = items.filter((r) => r.status === "stuck").length;
    // Most common pending responsibility
    const respCounts = new Map<string, number>();
    for (const r of items) {
      for (const resp of r.pending_responsibility) {
        respCounts.set(resp, (respCounts.get(resp) ?? 0) + 1);
      }
    }
    const top = Array.from(respCounts.entries()).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      attention: items.filter((r) => r.status === "in_progress").length,
      critical: stuck,
      top_resp: top?.[0],
      top_resp_count: top?.[1] ?? 0,
      first_stuck: items.find((r) => r.status === "stuck"),
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("eos.clearance_center.title", "Exit Clearance Center")}
          subtitle={t(
            "eos.clearance_center.subtitle",
            "Every active separation's clearance items by responsible party. Tashghil tracks days-outstanding and surfaces stuck items.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["all", "in_progress", "stuck", "complete"] as StatusFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  statusFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`eos.clearance_center.filter.${f}`, f)}
                {f === "stuck" && counts.critical > 0 && (
                  <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-semibold text-white">
                    {counts.critical}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "eos.clearance_center.cta.add_note",
                  "Add Clearance Item drawer: pick termination + item type + responsible party.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("eos.clearance_center.cta.add", "Add Clearance Item")}
          </button>
        </div>

        {counts.critical > 0 && counts.first_stuck && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<AlertTriangle className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "eos.clearance_center.suggestion.stuck_headline",
              `${counts.critical} clearance process${counts.critical === 1 ? "" : "es"} stuck >14 days.`,
            )}
            body={t(
              "eos.clearance_center.suggestion.stuck_body",
              counts.top_resp
                ? `Top pending department: ${counts.top_resp} (${counts.top_resp_count} processes). Tashghil can send a Khidmat reminder.`
                : "Tashghil suggests sending reminders or escalating to department heads.",
            )}
            primaryAction={{
              label: t("eos.clearance_center.suggestion.open_eos", "Open EOS detail"),
              onClick: () =>
                window.location.assign(`/operations/eos/${counts.first_stuck!.termination_id}`),
            }}
            suggestionKey={`clearance-stuck-${counts.first_stuck.termination_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "eos.clearance_center.suggestion.empty_headline",
              "No active clearance processes.",
            )}
            body={t(
              "eos.clearance_center.suggestion.empty_body",
              "Tashghil notes: when a termination initiates, the M18 workflow seeds default clearance items here.",
            )}
            primaryAction={{
              label: t("eos.clearance_center.suggestion.open_eos_list", "Active separations"),
              onClick: () => window.location.assign("/operations/eos"),
            }}
            suggestionKey="clearance-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("eos.clearance_center.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("eos.clearance_center.col.term_date", "Termination date")}</th>
                  <th className="px-3 py-2">{t("eos.clearance_center.col.progress", "Progress")}</th>
                  <th className="px-3 py-2">{t("eos.clearance_center.col.pending_resp", "Pending with")}</th>
                  <th className="px-3 py-2">{t("eos.clearance_center.col.days_out", "Days outstanding")}</th>
                  <th className="px-3 py-2">{t("eos.clearance_center.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.termination_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span dir="ltr">{row.termination_date}</span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "eos.clearance_center.trace.progress",
                              `Progress = items_complete (${row.items_complete}) + waived / items_total (${row.items_total}). 'stuck' fires when total>0 and days_outstanding>14 with pending items remaining.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono text-xs">
                          {row.items_complete} / {row.items_total}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.pending_responsibility.join(" · ") || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.status)}`}
                      >
                        {row.days_outstanding}d · {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <Link
                          to={`/operations/eos/${row.termination_id}`}
                          className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          {t("eos.clearance_center.action.view", "View detail")}
                        </Link>
                        {row.status !== "complete" && (
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "eos.clearance_center.action.remind_note",
                                  "Khidmat will WhatsApp the responsible party with the outstanding items.",
                                ),
                              )
                            }
                            className="rounded border border-amber-300 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-50"
                          >
                            <BellRing className="me-1 inline h-3 w-3" />
                            {t("eos.clearance_center.action.remind", "Remind")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("eos.clearance_center.title", "Exit Clearance"),
            window_label: counts.top_resp
              ? `top pending: ${counts.top_resp}`
              : t("eos.clearance_center.window_label_empty", "no pending"),
          }}
        />
      </aside>
    </div>
  );
}
